import { useEffect, useMemo } from 'react';
import { useCriteriaStore } from '../../stores/prioritization/criteriaStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { criterionForAttribute, criterionReadingOf, type CriterionReading } from '../../lib/criterionReading';

/** The workspace this hook last loaded criteria for (the store does not record it). */
let loadedForWorkspace: string | null | undefined;

/**
 * What an attribute's criterion says about itself in this workspace: for whom,
 * against what, and what its scale's ends mean (migration 967). The workspace's
 * criteria are loaded once per workspace, however many labels ask.
 */
export function useCriterionReading(attributeKey: string | null | undefined): CriterionReading {
  const criteria = useCriteriaStore((s) => s.criteria);
  // The workspace the store loads and saves for; the tenant store's id re-renders on a switch.
  const tenantWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceId = getCurrentWorkspaceId() ?? tenantWorkspaceId;

  useEffect(() => {
    // Read the live state, so many labels mounting at once start one load, not one each.
    const { syncState, fetchFromSupabase } = useCriteriaStore.getState();
    if (syncState.isLoading || loadedForWorkspace === workspaceId) return;
    loadedForWorkspace = workspaceId;
    void fetchFromSupabase().catch(() => {});
  }, [workspaceId]);

  return useMemo(
    () => criterionReadingOf(attributeKey ? criterionForAttribute(criteria, attributeKey, workspaceId) : undefined),
    [criteria, attributeKey, workspaceId],
  );
}

/**
 * Whether this workspace has its own criteria row for the attribute. Until it
 * does, the first reading saved creates that row — an insert the database allows
 * only to a holder of criteria.create (migration 1000); after that, saving a
 * reading updates the row (criteria.edit).
 */
export function useCriterionIsWorkspaceOwn(attributeKey: string | null | undefined): boolean {
  const criteria = useCriteriaStore((s) => s.criteria);
  const tenantWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceId = getCurrentWorkspaceId() ?? tenantWorkspaceId;
  return useMemo(() => {
    if (!attributeKey || !workspaceId) return false;
    return criterionForAttribute(criteria, attributeKey, workspaceId)?.workspaceId === workspaceId;
  }, [criteria, attributeKey, workspaceId]);
}
